import { Tooltip, TooltipContent, TooltipTrigger } from '@askable/ui/core/tooltip';
import { cn } from '@askable/ui/lib/utils';
import { Link } from 'src/lib/routing';

type MenuItemProps = {
  href: string;
  icon: React.ElementType;
  isActive?: boolean;
  isCollapsed?: boolean;
  isExternal?: boolean;
  label: string;
};

/*
 * Shared class for nav links and buttons: MenuItem, CreateStudy, HelpAndSupport, Notifications.
 *
 * Three tiers, following Linear: at rest a row is muted and unfilled, so the one
 * active row is the only white thing in the sidebar. Hover lays down a wash and
 * lifts the label; the active row deepens the wash and goes full --foreground.
 *
 * Row height and label size follow Linear's sidebar — 28px rows separated by
 * 1px, a 13px label. The icon box and its gap are left alone: every row in
 * the sidebar shares them, so they are what keeps the icons and labels on
 * one vertical line.
 *
 * Weight is constant across all three. Linear runs its sidebar labels at 500 in
 * every state and lets colour and fill carry the active row on their own —
 * shifting weight as well makes the row jump as the glyphs re-lay-out.
 *
 * The fills are --foreground at low alpha rather than --accent/--highlight,
 * because those are sized against the card layer and these rows sit on
 * --sidebar, several points below it — a fixed fill that reads as a whisper on a
 * card shouts on the sidebar, and in light mode it lands inside a point of the
 * near-white ground and vanishes. A wash is measured from whatever it covers, so
 * it holds its weight on either ground and inverts with the theme for free.
 */
/*
 * Collapsed, a row has no label to stretch to, so it becomes a 28px square —
 * the fill under hover and the active state read as a square rather than a
 * rectangle with the icon parked at one end. gap-0 matters: the label keeps its
 * fade, so it is still in the row and any gap would shove the icon off centre.
 *
 * ml-1 rather than centring the square in the list: the row takes its collapsed
 * width the moment the class flips, while the sidebar is still animating down
 * from its full width — centred, it would start out near the middle of a 256px
 * rail and slide left for 150ms. Offset from the leading edge, it lands on x=22
 * immediately and stays there, which is where the expanded rows put it too.
 *
 * The `before:` box is the hit area. A click on the rail outside a row expands the
 * sidebar, so the box extends 16px on each side to the rail edges (12px list inset
 * + 4px ml-1), and 1px up and down to close the 2px gap between rows.
 */
export const menuItemCollapsedClass =
  'ml-1 w-7 justify-center gap-0 px-0 before:absolute before:-inset-x-4 before:-inset-y-px';

export const menuItemClass =
  'group/menuitem relative cursor-pointer transition-colors flex h-7 w-full items-center gap-2 rounded-sm px-2 text-[13px] font-medium text-nav-foreground transition-all hover:bg-foreground/[0.04] hover:text-foreground dark:hover:bg-foreground/[0.065] focus-visible:bg-foreground/[0.04] focus-visible:text-foreground dark:focus-visible:bg-foreground/[0.065] active:bg-foreground/[0.065]';

export const MenuItem = ({ label, href, icon, isActive, isCollapsed }: MenuItemProps) => {
  const Icon = icon;

  const menuItem = (
    <Link
      to={href}
      className={cn(menuItemClass, {
        'bg-foreground/[0.065] text-foreground hover:bg-foreground/[0.065] dark:bg-foreground/[0.11] dark:hover:bg-foreground/[0.11]':
          isActive,
        [menuItemCollapsedClass]: isCollapsed,
      })}
    >
      <div className="shrink-0 p-[2px]">
        <Icon className="size-4" />
      </div>
      <div
        className={cn('truncate whitespace-nowrap opacity-100 transition-opacity duration-150', {
          'w-0 opacity-0': isCollapsed,
        })}
      >
        {label}
      </div>
    </Link>
  );

  return isCollapsed ? (
    <Tooltip>
      <TooltipTrigger asChild>{menuItem}</TooltipTrigger>
      <TooltipContent side="right" align="center">
        {label}
      </TooltipContent>
    </Tooltip>
  ) : (
    menuItem
  );
};
